import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { useLanguage } from '../Layout/LanguageContext';
import { 
  FaChartLine, 
  FaCogs, 
  FaBrain
} from 'react-icons/fa';

const ServicesSection = styled.section`
  padding: 6rem 2rem;
  background: var(--background-color);
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 4rem 1rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  @media (max-width: 768px) {
    margin-bottom: 3rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-top: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ServiceCard = styled(motion.div)`
  background: var(--background-light);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 8px 32px rgba(0, 51, 102, 0.1);
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 16px 48px rgba(0, 51, 102, 0.15);
    border-color: var(--accent-color);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover::before {
    transform: scaleX(1);
  }

  @media (max-width: 768px) {
    padding: 2rem;
  }
`;

const ServiceIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  font-size: 2rem;
  color: white;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
    z-index: -1;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, var(--accent-color), var(--accent-light));
    opacity: 0;
    transition: opacity 0.3s ease;
    z-index: -1;
  }

  &:hover::after {
    opacity: 1;
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
  line-height: 1.3;
`;

const ServiceDescription = styled.p`
  font-size: 1rem;
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const TechnologiesList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const TechnologyTag = styled.span`
  background: var(--background-dark);
  color: var(--primary-color);
  padding: 0.4rem 0.8rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 500;
  border: 1px solid var(--background-dark);
  transition: all 0.3s ease;

  &:hover {
    background: var(--primary-color);
    color: white;
    border-color: var(--primary-color);
    transform: translateY(-2px);
  }
`;

const servicesContent = {
  de: {
    title: 'Meine Kompetenzfelder',
    subtitle: 'Ich biete maßgeschneiderte Lösungen in drei zentralen Bereichen der modernen IT-Beratung.',
    dataAnalytics: {
      title: 'Data & Analytics',
      description: 'Ich verwandle Ihre Rohdaten in strategische Vermögenswerte. Vom Aufbau eines soliden Datenfundaments bis zur Erstellung interaktiver Dashboards, die Ihnen helfen, fundierte Entscheidungen zu treffen.',
      technologies: ['Power BI', 'MS SQL Server', 'Azure Data Factory', 'Databricks', 'Fabric', 'T-SQL', 'Python']
    },
    processAutomation: {
      title: 'Prozessautomatisierung',
      description: 'Ich analysiere und digitalisiere Ihre manuellen Geschäftsprozesse. Mit intelligenten Workflows steigern wir die Effizienz, reduzieren Fehler und schaffen Freiräume für Ihre Mitarbeiter.',
      technologies: ['Microsoft Power Platform', 'Power Automate', 'Power Apps', 'Azure Pipelines', 'SharePoint']
    },
    aiSolutions: {
      title: 'KI-Lösungen & Copilots',
      description: 'Nutzen Sie das Potenzial Künstlicher Intelligenz. Ich entwickle für Sie maßgeschneiderte KI-gestützte Lösungen – von Chatbot-Agenten bis zur intelligenten Analyse unstrukturierter Daten wie Audio-Transkriptionen.',
      technologies: ['Azure AI Foundry', 'Azure AI Speech', 'Copilot Studio', 'n8n', 'ChatGPT', 'Cursor']
    }
  },
  en: {
    title: 'My Areas of Expertise',
    subtitle: 'I offer tailored solutions in three core areas of modern IT consulting.',
    dataAnalytics: {
      title: 'Data & Analytics',
      description: 'I transform your raw data into strategic assets. From building a solid data foundation to creating interactive dashboards that help you make informed decisions.',
      technologies: ['Power BI', 'MS SQL Server', 'Azure Data Factory', 'Databricks', 'Fabric', 'T-SQL', 'Python']
    },
    processAutomation: {
      title: 'Process Automation',
      description: 'I analyze and digitize your manual business processes. With intelligent workflows, we increase efficiency, reduce errors, and create freedom for your employees.',
      technologies: ['Microsoft Power Platform', 'Power Automate', 'Power Apps', 'Azure Pipelines', 'SharePoint']
    },
    aiSolutions: {
      title: 'AI Solutions & Copilots',
      description: 'Leverage the potential of Artificial Intelligence. I develop customized AI-powered solutions for you – from chatbot agents to intelligent analysis of unstructured data like audio transcriptions.',
      technologies: ['Azure AI Foundry', 'Azure AI Speech', 'Copilot Studio', 'n8n', 'ChatGPT', 'Cursor']
    }
  }
};

const Services: React.FC = () => {
  const { language } = useLanguage();
  const t = servicesContent[language];
  const [ref] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });



  return (
    <ServicesSection id="services" ref={ref}>
      <Container>
        <SectionHeader>
          <SectionTitle>{t.title}</SectionTitle>
          <SectionSubtitle>{t.subtitle}</SectionSubtitle>
        </SectionHeader>

        <ServicesGrid>
          <ServiceCard>
            <ServiceIcon>
              <FaChartLine />
            </ServiceIcon>
            <ServiceTitle>{t.dataAnalytics.title}</ServiceTitle>
            <ServiceDescription>{t.dataAnalytics.description}</ServiceDescription>
            <TechnologiesList>
              {t.dataAnalytics.technologies.map((tech, index) => (
                <TechnologyTag key={index}>{tech}</TechnologyTag>
              ))}
            </TechnologiesList>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>
              <FaCogs />
            </ServiceIcon>
            <ServiceTitle>{t.processAutomation.title}</ServiceTitle>
            <ServiceDescription>{t.processAutomation.description}</ServiceDescription>
            <TechnologiesList>
              {t.processAutomation.technologies.map((tech, index) => (
                <TechnologyTag key={index}>{tech}</TechnologyTag>
              ))}
            </TechnologiesList>
          </ServiceCard>

          <ServiceCard>
            <ServiceIcon>
              <FaBrain />
            </ServiceIcon>
            <ServiceTitle>{t.aiSolutions.title}</ServiceTitle>
            <ServiceDescription>{t.aiSolutions.description}</ServiceDescription>
            <TechnologiesList>
              {t.aiSolutions.technologies.map((tech, index) => (
                <TechnologyTag key={index}>{tech}</TechnologyTag>
              ))}
            </TechnologiesList>
          </ServiceCard>
        </ServicesGrid>
      </Container>
    </ServicesSection>
  );
};

export default Services;



